import {Component, ElementRef, inject, OnDestroy, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {ImageViewerItemFragment} from "@app/common/components/viewers/image-viewer/image-viewer.generated"
import {ImageViewerItemsGQL} from "@common/components/viewers/image-viewer/image-viewer.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {MimeType, UtilsService} from "@legacy/helpers/utils"
import {IsNonNull, IsNotUndefined} from "@cm/utils"

// TODO: upgrade Photoswipe and use ESM imports once non-GQL version has been removed
declare class PhotoSwipe {
    constructor(element: Element, photoSwipeUI: unknown, items: unknown[], options: unknown)
    init: () => void
    getCurrentIndex: () => number
}
declare const PhotoSwipeUI_Default: unknown

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-image-viewer",
    templateUrl: "image-viewer.component.html",
    styleUrls: ["image-viewer.component.scss"],
})
export class ImageViewerComponent implements OnDestroy {
    readonly $photoSwipeElement = viewChild.required<ElementRef>("photoSwipeElement")
    public options = {
        index: 0,
        closeOnScroll: true,
        shareEl: false,
        captionEl: true,
        loop: false,
    }

    private readonly imageViewerItems = inject(ImageViewerItemsGQL)

    items: ImageViewerItemFragment[] = []

    gallery: {init: () => void; getCurrentIndex: () => number} | null = null

    ngOnDestroy() {
        this.gallery = null
    }

    get currentIndex() {
        return this.gallery?.getCurrentIndex() ?? 0
    }

    async openViewer(currentDataObjectId: string, dataObjectIds: string[]) {
        const validDataObjectIds = dataObjectIds.filter(IsNotUndefined)
        const {dataObjects: imageViewerItems} = await fetchThrowingErrors(this.imageViewerItems)({ids: validDataObjectIds})
        // The dataObjects(id: {in: ...}) query does not preserve the requested id order, so re-sort the
        // results back into the caller's order before building the gallery.
        const itemsById = new Map(imageViewerItems.filter(IsNonNull).map((item) => [item.id, item]))
        this.items = validDataObjectIds
            .map((id) => itemsById.get(id))
            .filter(IsNotUndefined)
            .filter((imageViewerItem) => UtilsService.mimeTypeMatch(MimeType.Images, imageViewerItem.mediaType))
            .filter((item) => !!item.thumbnail?.downloadUrl)
        if (!this.items.length) {
            return
        }
        const galleryItems = this.items.map((imageViewerItem) => ({
            src: imageViewerItem.thumbnail?.downloadUrl,
            w: imageViewerItem.width,
            h: imageViewerItem.height,
            title: imageViewerItem.originalFileName,
        }))
        this.options.index = this.items.findIndex(({id}) => id === currentDataObjectId) ?? 0
        this.gallery = new PhotoSwipe(this.$photoSwipeElement().nativeElement, PhotoSwipeUI_Default, galleryItems, this.options)
        this.gallery?.init()
    }
}
